import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const GestionProfesores = () => {
  const { user, API } = useContext(AuthContext);

  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProfesor, setCurrentProfesor] = useState(null);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [draggedProfesor, setDraggedProfesor] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    foto_url: '',
    ciudad: '',
    pais: '',
    biografia: '',
    especialidades: [],
    email: '',
    telefono: '',
    activo: true,
    orden: 0
  });

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadProfesores();
    }
  }, [user]);

  const loadProfesores = async () => {
    try {
      const response = await axios.get(`${API}/admin/profesores`);
      setProfesores(response.data);
    } catch (error) {
      console.error('Error loading profesores:', error);
      toast.error('Error al cargar profesores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setCurrentProfesor(null);
    setFormData({
      nombre: '',
      apellido: '',
      foto_url: '',
      ciudad: '',
      pais: '',
      biografia: '',
      especialidades: [],
      email: '',
      telefono: '',
      activo: true,
      orden: profesores.length
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (profesor) => {
    setIsEditing(true);
    setCurrentProfesor(profesor);
    setFormData({
      nombre: profesor.nombre,
      apellido: profesor.apellido,
      foto_url: profesor.foto_url || '',
      ciudad: profesor.ciudad,
      pais: profesor.pais,
      biografia: profesor.biografia,
      especialidades: profesor.especialidades || [],
      email: profesor.email || '',
      telefono: profesor.telefono || '',
      activo: profesor.activo,
      orden: profesor.orden
    });
    setIsDialogOpen(true);
  };

  const handleFotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La foto debe ser menor a 5MB');
      return;
    }

    setUploadingFoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/class-banner`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, foto_url: response.data.banner_url });
      toast.success('Foto subida exitosamente');
    } catch (error) {
      console.error('Error uploading foto:', error);
      toast.error('Error al subir foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🟢 FORM SUBMIT TRIGGERED');
    console.log('🔍 Form data:', formData);
    console.log('📝 Nombre:', formData.nombre);
    console.log('📝 Apellido:', formData.apellido);
    console.log('📝 Ciudad:', formData.ciudad);
    console.log('📝 País:', formData.pais);
    console.log('📝 Biografía length:', formData.biografia.length);

    // Validación manual
    if (!formData.nombre || formData.nombre.trim().length < 2) {
      toast.error('El nombre es requerido (mínimo 2 caracteres)');
      return;
    }
    
    if (!formData.apellido || formData.apellido.trim().length < 2) {
      toast.error('El apellido es requerido (mínimo 2 caracteres)');
      return;
    }
    
    if (!formData.ciudad || formData.ciudad.trim().length < 2) {
      toast.error('La ciudad es requerida (mínimo 2 caracteres)');
      return;
    }
    
    if (!formData.pais || formData.pais.trim().length === 0) {
      toast.error('El país es requerido');
      return;
    }
    
    if (!formData.biografia || formData.biografia.trim().length < 10) {
      toast.error('La biografía es requerida (mínimo 10 caracteres)');
      return;
    }

    console.log('✅ Validación pasada, enviando al backend...');

    try {
      if (isEditing && currentProfesor) {
        console.log('🔄 Updating profesor:', currentProfesor.id);
        const response = await axios.put(`${API}/admin/profesores/${currentProfesor.id}`, formData);
        console.log('✅ Update response:', response.data);
        toast.success('Profesor actualizado exitosamente');
      } else {
        console.log('✨ Creating new profesor');
        console.log('📡 API URL:', `${API}/admin/profesores`);
        const response = await axios.post(`${API}/admin/profesores`, formData);
        console.log('✅ Profesor created:', response.data);
        toast.success('Profesor creado exitosamente');
      }

      setIsDialogOpen(false);
      await loadProfesores();
    } catch (error) {
      console.error('❌ ERROR COMPLETO:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Error al guardar profesor');
    }
  };

  const handleDelete = async (profesorId) => {
    if (!window.confirm('¿Estás seguro de eliminar este profesor?')) return;

    try {
      await axios.delete(`${API}/admin/profesores/${profesorId}`);
      toast.success('Profesor eliminado exitosamente');
      loadProfesores();
    } catch (error) {
      console.error('Error deleting profesor:', error);
      toast.error('Error al eliminar profesor');
    }
  };

  const handleToggleActivo = async (profesor) => {
    try {
      await axios.put(`${API}/admin/profesores/${profesor.id}`, {
        activo: !profesor.activo
      });
      toast.success(`Profesor ${!profesor.activo ? 'activado' : 'desactivado'} exitosamente`);
      loadProfesores();
    } catch (error) {
      console.error('Error toggling profesor:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleAddEspecialidad = () => {
    const especialidad = prompt('Ingrese la especialidad:');
    if (especialidad && especialidad.trim()) {
      setFormData({
        ...formData,
        especialidades: [...formData.especialidades, especialidad.trim()]
      });
    }
  };

  const handleRemoveEspecialidad = (index) => {
    const newEspecialidades = formData.especialidades.filter((_, i) => i !== index);
    setFormData({ ...formData, especialidades: newEspecialidades });
  };

  // Funciones de Drag and Drop para reordenar profesores
  const handleDragStart = (e, profesor, index) => {
    setDraggedProfesor({ profesor, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedProfesor || draggedProfesor.index === dropIndex) {
      setDraggedProfesor(null);
      return;
    }

    // Reordenar localmente primero
    const newProfesores = [...profesores];
    const [removed] = newProfesores.splice(draggedProfesor.index, 1);
    newProfesores.splice(dropIndex, 0, removed);
    
    // Actualizar orden en el array
    const profesoresConOrden = newProfesores.map((p, idx) => ({ ...p, orden: idx }));
    setProfesores(profesoresConOrden);
    setDraggedProfesor(null);

    // Guardar el nuevo orden en el backend
    try {
      const ordenData = profesoresConOrden.map((p, idx) => ({ id: p.id, orden: idx }));
      console.log('Enviando reorden:', ordenData);
      
      const response = await axios.put(`${API}/admin/profesores/reordenar`, { profesores: ordenData }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('Respuesta reorden:', response.data);
      
      if (response.data.success) {
        toast.success('Orden actualizado');
      } else {
        throw new Error('No se pudo guardar el orden');
      }
    } catch (error) {
      console.error('Error al reordenar:', error);
      toast.error('Error al guardar el orden');
      loadProfesores(); // Recargar si hay error
    }
  };

  const handleDragEnd = () => {
    setDraggedProfesor(null);
    setDragOverIndex(null);
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-red-600">Acceso denegado. Solo administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="text-center py-8">
            <i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i>
            <p className="mt-4">Cargando profesores...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header mejorado */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                      <i className="fas fa-chalkboard-teacher text-2xl text-indigo-600"></i>
                    </div>
                    <h1 className="text-3xl font-bold text-white">Gestión de Profesores</h1>
                  </div>
                  <p className="text-indigo-100 ml-15">Administra el equipo docente y staff del instituto</p>
                </div>
                <Button 
                  onClick={handleCreate} 
                  className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Nuevo Profesor
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Instrucciones de reordenamiento */}
        {profesores.length > 1 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
            <i className="fas fa-arrows-alt text-blue-500"></i>
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Arrastra las tarjetas para cambiar el orden en que aparecen los profesores
            </p>
          </div>
        )}

        {/* Grid de profesores */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {profesores.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-inbox text-5xl text-indigo-400"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No hay profesores registrados</h3>
                <p className="text-gray-500 mb-6">Comienza agregando el primer profesor del equipo</p>
                <Button onClick={handleCreate} className="instituto-gradient text-white px-6 py-3">
                  <i className="fas fa-plus mr-2"></i>
                  Crear Primer Profesor
                </Button>
              </div>
            </div>
          ) : (
            profesores.map((profesor, index) => (
              <div 
                key={profesor.id} 
                draggable
                onDragStart={(e) => handleDragStart(e, profesor, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 overflow-hidden cursor-grab active:cursor-grabbing ${!profesor.activo ? 'opacity-60' : ''} ${dragOverIndex === index ? 'border-purple-500 scale-105' : 'border-gray-200'} ${draggedProfesor?.profesor.id === profesor.id ? 'opacity-50' : ''}`}
              >
                {/* Indicador de arrastre - franja reducida */}
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 h-6 relative">
                  <div className="absolute top-0.5 left-2 bg-white/20 rounded px-1.5 py-0.5 text-white text-xs flex items-center gap-1">
                    <i className="fas fa-grip-vertical text-[10px]"></i>
                    #{index + 1}
                  </div>
                </div>
                
                <div className="px-6 pb-6 -mt-0">
                  {/* Foto del profesor */}
                  <div className="flex justify-center mb-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-white ring-4 ring-white shadow-xl">
                      {profesor.foto_url ? (
                        <img
                          src={profesor.foto_url.startsWith('http') ? profesor.foto_url : `${API}${profesor.foto_url}`}
                          alt={`${profesor.nombre} ${profesor.apellido}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-indigo-200 to-purple-200 flex items-center justify-center"><i class="fas fa-user text-3xl text-indigo-600"></i></div>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-indigo-200 to-purple-200 flex items-center justify-center">
                          <i className="fas fa-user text-3xl text-indigo-600"></i>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Información del profesor */}
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {profesor.nombre} {profesor.apellido}
                    </h3>

                    {/* Ubicación */}
                    <div className="flex items-center justify-center gap-2 text-gray-600 mb-3">
                      <i className="fas fa-map-marker-alt text-indigo-500"></i>
                      <span className="text-sm">{profesor.ciudad}, {profesor.pais}</span>
                    </div>

                    {/* Estado */}
                    <div className="flex justify-center mb-3">
                      <Badge 
                        variant={profesor.activo ? 'default' : 'secondary'} 
                        className={`${profesor.activo ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-600'} px-3 py-1`}
                      >
                        <i className={`fas ${profesor.activo ? 'fa-check-circle' : 'fa-eye-slash'} mr-1`}></i>
                        {profesor.activo ? 'Visible' : 'Oculto'}
                      </Badge>
                    </div>

                    {/* Especialidades */}
                    {profesor.especialidades && profesor.especialidades.length > 0 && (
                      <div className="flex flex-wrap gap-2 justify-center mb-4">
                        {profesor.especialidades.map((esp, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 border border-indigo-200 text-xs"
                          >
                            {esp}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Biografía preview */}
                    {profesor.biografia && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4 px-2">
                        {profesor.biografia}
                      </p>
                    )}
                  </div>

                  {/* Botones de acción */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActivo(profesor)}
                      className={`flex-1 ${profesor.activo ? 'border-green-300 text-green-600 hover:bg-green-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                    >
                      <i className={`fas ${profesor.activo ? 'fa-eye' : 'fa-eye-slash'} mr-1`}></i>
                      {profesor.activo ? 'Ocultar' : 'Mostrar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(profesor)}
                      className="flex-1 border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                    >
                      <i className="fas fa-edit mr-1"></i>
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(profesor.id)}
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <i className="fas fa-trash mr-1"></i>
                      Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Dialog para Crear/Editar - DISEÑO MEJORADO PREMIUM */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto p-0">
          {/* Header con gradiente mejorado */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6 sticky top-0 z-10">
            <DialogHeader>
              <DialogTitle className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                  <i className={`fas ${isEditing ? 'fa-user-edit' : 'fa-user-plus'} text-2xl text-indigo-600`}></i>
                </div>
                {isEditing ? 'Editar Profesor' : 'Nuevo Profesor'}
              </DialogTitle>
              <p className="text-indigo-100 mt-2 ml-15">
                {isEditing ? 'Actualiza la información del profesor o staff' : 'Completa los datos del nuevo miembro del equipo'}
              </p>
            </DialogHeader>
          </div>
          
          <form id="profesor-form" onSubmit={handleSubmit} className="p-8 space-y-8 bg-gradient-to-br from-gray-50 to-indigo-50">
            {/* Foto del Profesor - Sección destacada */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-indigo-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-camera text-white"></i>
                  </div>
                  Fotografía del Profesor
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Esta imagen se mostrará en la landing page</p>
              </div>
              
              <div className="p-6">
                {/* Preview de foto mejorado */}
                {formData.foto_url && (
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-40 h-40 rounded-full overflow-hidden ring-4 ring-indigo-200 shadow-2xl">
                        <img
                          src={formData.foto_url.startsWith('http') ? formData.foto_url : `${API}${formData.foto_url}`}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-indigo-200 to-purple-200 flex items-center justify-center"><i class="fas fa-user text-5xl text-indigo-400"></i></div>';
                          }}
                        />
                      </div>
                      <div className="absolute bottom-2 right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white">
                        <i className="fas fa-check text-white"></i>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Opción 1: Subir desde PC */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <i className="fas fa-upload text-indigo-600"></i>
                      Opción 1: Subir desde PC
                    </Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFotoUpload}
                      className="hidden"
                      id="foto-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('foto-upload').click()}
                      disabled={uploadingFoto}
                      className="w-full h-14 text-base border-2 border-dashed border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-300"
                    >
                      {uploadingFoto ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2 text-xl"></i>
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt mr-2 text-2xl text-indigo-600"></i>
                          {formData.foto_url ? 'Cambiar Foto' : 'Seleccionar Foto'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                      <i className="fas fa-info-circle text-indigo-500"></i>
                      JPG, PNG • Máximo 5MB
                    </p>
                  </div>
                  
                  {/* Opción 2: URL externa */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <i className="fas fa-link text-purple-600"></i>
                      Opción 2: URL de Imagen
                    </Label>
                    <Input
                      type="url"
                      placeholder="https://i.imgur.com/ejemplo.jpg"
                      value={formData.foto_url || ''}
                      onChange={(e) => setFormData({ ...formData, foto_url: e.target.value })}
                      className="h-14 border-2 border-purple-200 focus:border-purple-400"
                    />
                    <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                      <i className="fas fa-info-circle text-purple-500"></i>
                      Pega la URL completa de Imgur u otro servicio
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Información Personal */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4 border-b border-blue-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-id-card text-white"></i>
                  </div>
                  Información Personal
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Datos básicos del profesor</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre" className="text-sm font-semibold text-gray-700">
                      Nombre <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-user text-gray-400"></i>
                      </div>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                        placeholder="Juan"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-blue-400"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="apellido" className="text-sm font-semibold text-gray-700">
                      Apellido <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-user text-gray-400"></i>
                      </div>
                      <Input
                        id="apellido"
                        value={formData.apellido}
                        onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                        required
                        placeholder="Pérez"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-blue-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-map-marker-alt text-white"></i>
                  </div>
                  Ubicación
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Ciudad y país del profesor</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="ciudad" className="text-sm font-semibold text-gray-700">
                      Ciudad <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-city text-gray-400"></i>
                      </div>
                      <Input
                        id="ciudad"
                        value={formData.ciudad}
                        onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                        required
                        placeholder="Montevideo"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-green-400"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="pais" className="text-sm font-semibold text-gray-700">
                      País <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-flag text-gray-400"></i>
                      </div>
                      <Input
                        id="pais"
                        value={formData.pais}
                        onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                        required
                        placeholder="Uruguay"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-green-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Biografía */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-amber-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-file-alt text-white"></i>
                  </div>
                  Biografía / Experiencia
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Formación académica y trayectoria ministerial</p>
              </div>
              
              <div className="p-6 space-y-3">
                <Textarea
                  id="biografia"
                  value={formData.biografia}
                  onChange={(e) => setFormData({ ...formData, biografia: e.target.value })}
                  required
                  rows={5}
                  placeholder="Ejemplo: Licenciado en Teología, con 15 años de experiencia en ministerio pastoral. Ha servido como pastor principal en..."
                  className="resize-none border-2 border-gray-200 focus:border-amber-400"
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="fas fa-info-circle text-amber-500"></i>
                    Esta información se mostrará en la landing page
                  </p>
                  <p className="text-xs text-gray-400">
                    {formData.biografia.length} caracteres
                  </p>
                </div>
              </div>
            </div>

            {/* Especialidades */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-purple-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-star text-white"></i>
                  </div>
                  Especialidades / Áreas de Enseñanza
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Materias y temas en los que se especializa</p>
              </div>
              
              <div className="p-6">
                {formData.especialidades.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.especialidades.map((esp, idx) => (
                      <Badge key={idx} className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200 px-4 py-2 text-sm flex items-center gap-2">
                        {esp}
                        <button
                          type="button"
                          onClick={() => handleRemoveEspecialidad(idx)}
                          className="ml-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full w-5 h-5 flex items-center justify-center transition-colors"
                          title="Eliminar especialidad"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddEspecialidad}
                  className="border-dashed border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50 h-12 px-6"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Agregar Especialidad
                </Button>
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <i className="fas fa-info-circle text-purple-500"></i>
                  Ejemplo: Teología Sistemática, Homilética, Historia de la Iglesia, etc.
                </p>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-6 py-4 border-b border-teal-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <i className="fas fa-envelope text-white"></i>
                  </div>
                  Información de Contacto
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Email y teléfono (opcional)</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                      Email
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-envelope text-gray-400"></i>
                      </div>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="profesor@ejemplo.com"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-teal-400"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="telefono" className="text-sm font-semibold text-gray-700">
                      Teléfono
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-phone text-gray-400"></i>
                      </div>
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="+598 99 123 456"
                        className="pl-10 h-12 border-2 border-gray-200 focus:border-teal-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Visibilidad */}
            <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-600 to-slate-700 rounded-lg flex items-center justify-center">
                    <i className="fas fa-eye text-white"></i>
                  </div>
                  Visibilidad en Landing Page
                </h3>
                <p className="text-sm text-gray-600 mt-1 ml-10">Controla si el profesor aparece públicamente</p>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border-2 border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${formData.activo ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'} shadow-lg`}>
                      <i className={`fas ${formData.activo ? 'fa-eye' : 'fa-eye-slash'} text-white text-xl`}></i>
                    </div>
                    <div>
                      <Label htmlFor="activo" className="text-base font-bold text-gray-800 cursor-pointer block">
                        {formData.activo ? 'Profesor Visible' : 'Profesor Oculto'}
                      </Label>
                      <p className="text-sm text-gray-600">
                        {formData.activo ? 'Se mostrará en la landing page' : 'No aparecerá públicamente'}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="activo"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-16 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-8 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-green-500 peer-checked:to-emerald-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Botones de acción mejorados */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t-2 border-gray-200 sticky bottom-0 bg-gradient-to-br from-gray-50 to-indigo-50 p-6 rounded-2xl shadow-xl">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="flex-1 h-14 text-base border-2 border-gray-300 hover:bg-gray-100 font-semibold"
              >
                <i className="fas fa-times mr-2 text-lg"></i>
                Cancelar
              </Button>
              <Button 
                type="submit"
                form="profesor-form"
                className="flex-1 h-14 text-base bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus-circle'} mr-2 text-lg`}></i>
                {isEditing ? 'Guardar Cambios' : 'Crear Profesor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionProfesores;
